import React, { useEffect, useState } from 'react';

import { LinkButton } from '../blocks/Button.jsx';
import Columns from '../blocks/Columns.jsx';
import { ContactImageComponent } from '../blocks/ContactImage.jsx';
import { ContactMetaComponent } from '../blocks/ContactMeta.jsx';
import Divider from '../blocks/Divider.jsx';
import Footer from '../blocks/Footer.jsx';
import Image from '../blocks/Image.jsx';
import * as InventoryCell from '../blocks/InventoryCell.jsx';
import { InventoryImageComponent } from '../blocks/InventoryImage.jsx';
import { InventoryMetaComponent } from '../blocks/InventoryMeta.jsx';
import * as InventoryList from '../blocks/InventoryList.jsx';
import Logo from '../blocks/Logo.jsx';
import Name from './Name.jsx';
import { RecipientMetaComponent } from '../blocks/RecipientMeta.jsx';
import Social from '../blocks/Social.jsx';
import { HeaderText, TokenizedText } from '../blocks/Text.jsx';

import { Email } from '../../components/repositories/Type.jsx';

function GetComponentAndData(block, providedData, context) {
    if (!block || !block.type) {
        return null;
    }
    let data = block;
    let Component;
    switch (block.type.value) {
        case Name.Block.Column:
            Component = Columns.Component;
            data = providedData;
            break;
        case Name.Block.Contact.Image:
            Component = ContactImageComponent;
            data = providedData;
            break;
        case Name.Block.Contact.Message:
        case Name.Block.Contact.Text:
            Component = ContactMetaComponent;
            data = providedData;
            break;
        case Name.Block.Divider:
            Component = Divider.Component;
            break;
        case Name.Block.Footer:
            Component = Footer.Component;
            break;
        case Name.Block.HeaderText:
            Component = HeaderText.Component;
            break;
        case Name.Block.Image:
            Component = Image.Component;
            break;
        case Name.Block.Inventory.Button:
            Component = InventoryCell.InventoryButton.Component;
            data = providedData;
            break;
        case Name.Block.Inventory.Image:
            Component = InventoryImageComponent;
            data = providedData;
            break;
        case Name.Block.Inventory.List:
            switch (context) {
                case Email.value(Email.PurchaseConfirmation):
                case Email.PurchaseConfirmation:
                    Component = InventoryList.Transactional.Component;
                    data = providedData && { Inventory: providedData.Inventory };
                    break;
                default:
                    Component = InventoryList.Campaign.Component;
                    break;
            }
            break;
        case Name.Block.Inventory.Text:
            Component = InventoryMetaComponent;
            data = providedData;
            break;
        case Name.Block.LinkButton:
            Component = LinkButton.Component;
            break;
        case Name.Block.Logo:
            Component = Logo.Component;
            data = providedData;
            break;
        case Name.Block.Recipient.Text:
            Component = RecipientMetaComponent;
            data = providedData;
            break;
        case Name.Block.Social:
            Component = Social.Component;
            break;
        case Name.Block.Text:
        default:
            Component = TokenizedText.Component;
            break;
    }
    return { component: Component, data };
}

class BuilderBlock extends React.Component {
    constructor(props) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
    }

    shouldComponentUpdate(nextProps) {
        switch (nextProps.dataType) {
            case 'column':
            case 'layout':
            case 'layout-horizontal':
            case 'layout-vertical':
                return true;
        }
        return !nextProps.dragging;
    }

    handleClick(event) {
        if (event) {
            event.stopPropagation();
        }
        if (this.props.onBlockClick) {
            this.props.onBlockClick(this.props.blockId);
        }
    }

    render() {
        const props = this.props;
        let className = 'builder-block draggable';
        if (props.active) {
            className += ' draggable-active';
        }
        let dragOutline;
        switch (props.dataType) {
            case 'column':
            case 'layout':
            case 'layout-horizontal':
            case 'layout-vertical':
                className += ' layout';
                break;
            default:
                if (props.renderDragOutline != false) {
                    dragOutline = <span className="drag-outline" />;
                }
                break;
        }
        if (props.className) {
            className += ` ${props.className}`;
        }
        return (
            <div
                align="center"
                border={0}
                className={className}
                cellPadding={props.cellPadding || "0"}
                cellSpacing={props.cellSpacing || "0"}
                data-block-type={props.blockType}
                data-type={props.dataType}
                draggable={props.builder == true}
                height="100%"
                id={props.blockId}
                width="100%"
                onClick={this.handleClick}
                onDragEnter={props.onDragEnter}
                onDragStart={props.onDragStart}>
                {props.children}
                {dragOutline}
            </div>
        );
    }
}

function EmptyPlaceholder(props) {
    const style = { minHeight: props.minHeight || '40px', minWidth: props.minWidth || '10px' };
    if (props.fullWidth) {
        style.width = '100%';
    }
    return <div style={style}>{props.children}</div>
}

function PageBottomHiddenDraggable(props) {
    return (
        <React.Fragment>
            <div id="bottom-hidden-enter" className="bottom-hidden hidden draggable" onDragEnter={props.onDragEnter} />
            <div id="bottom-hidden-exit" className="bottom-hidden hidden draggable" onDragEnter={props.onDragEnter} />
        </React.Fragment>
    );
}

function PageTopHiddenDraggable(props) {
    const [exitOffset, setExitOffset] = useState(undefined);
    const [style, setStyle] = useState({ cursor: 'default' });
    useEffect(() => {
        const _style = { cursor: 'default' };
        if (props.pageEmpty) {
            _style.bottom = 0;
            _style.height = 'auto';
        } else {
            const paddingTop = parseFloat(props.offset);
            if (!isNaN(paddingTop) && paddingTop > 4.0) {
                _style.height = `${paddingTop}px`;
                if (props.adjustTop) {
                    _style.top = `-${paddingTop}px`;
                    setExitOffset(`-${30.0 + paddingTop}px`);
                }
            }
        }
        setStyle(_style);
    }, [props.offset, props.pageEmpty]);
    return (
        <React.Fragment>
            <div id="top-hidden-exit" className="hidden draggable" style={exitOffset != undefined ? { top: exitOffset } : undefined} onDragEnter={props.onDragEnter} />
            <div id="top-hidden-enter" className="hidden draggable" style={style} onDragEnter={props.onDragEnter} />
        </React.Fragment>
    );
}

function withBlockRendering(WrappedComponent) {
    return class extends React.Component {
        constructor(props) {
            super(props);

            this.handleBlockRendering = this.handleBlockRendering.bind(this);
        }

        handleBlockRendering(blocks, blockPropsHandler, builderBlockPropsHandler) {
            const renderedBlocks = [];
            if (!blocks) {
                return renderedBlocks
            }
            const props = this.props;
            blocks.forEach((d, i) => {
                let componentData = GetComponentAndData(d, props.data, props.pageType);
                if (!componentData || !componentData.component) {
                    return;
                }
                let blockProps = {};
                if (props.onBlockProps) {
                    blockProps = props.onBlockProps(d);
                    if (blockProps.block) {
                        componentData = GetComponentAndData(blockProps.block, props.data, props.pageType);
                    }
                }
                let additionalProps = {};
                if (blockPropsHandler) {
                    additionalProps = blockPropsHandler(d) || {};
                }
                const id = d.id || `${i}`;
                const blockConfig = blockProps.block ? blockProps.block.config : d.config;
                let block = (
                    <componentData.component
                        key={id}
                        active={blockProps.active}
                        baseUrl={props.baseUrl}
                        blockConfig={blockConfig}
                        blockHandler={blockPropsHandler}
                        blockId={id}
                        blocks={blockProps.block ? blockProps.block.blocks : d.blocks}
                        builder={props.builder}
                        builderBlockPropsHandler={builderBlockPropsHandler}
                        config={props.config}
                        data={componentData.data}
                        dataType={d.dataType}
                        disabled={props.disabled}
                        dragging={props.dragging}
                        frameDoc={props.frameDoc}
                        inLayout={props.inLayout}
                        pageConfig={props.pageConfig}
                        queryLayout={props.queryLayout}
                        querySub={props.querySub}
                        serverSide={props.serverSide}
                        type={props.type}
                        onBlockClick={props.onBlockClick}
                        onBlockProps={props.onBlockProps}
                        onDragEnter={props.onDragEnter}
                        onDragStart={props.onDragStart}
                        {...additionalProps} />
                );
                if (props.builder && !props.disabled) {
                    switch (d.dataType) {
                        case 'layout':
                        case 'layout-horizontal':
                        case 'layout-spacer':
                        case 'layout-vertical':
                            break;
                        default:
                            let builderBlockProps = {};
                            if (builderBlockPropsHandler) {
                                builderBlockProps = builderBlockPropsHandler(blockProps.block || d, blockConfig, props) || {};
                            }
                            block = (
                                <BuilderBlock
                                    key={id}
                                    active={blockProps.active}
                                    blockId={id}
                                    blockType={d.type && d.type.value}
                                    builder={props.builder}
                                    dataType={d.dataType}
                                    dragging={props.dragging}
                                    serverSide={props.serverSide}
                                    onBlockClick={props.onBlockClick}
                                    onDragEnter={props.onDragEnter}
                                    onDragStart={props.onDragStart}
                                    {...builderBlockProps}>
                                    {block}
                                </BuilderBlock>
                            );
                            break;
                    }
                }
                renderedBlocks.push(block);
            });
            return renderedBlocks;
        }

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    onBlockRendering={this.handleBlockRendering}>
                    {this.props.children}
                </WrappedComponent>
            );
        }
    }
}

export {
    BuilderBlock,
    EmptyPlaceholder,
    PageBottomHiddenDraggable,
    PageTopHiddenDraggable,
    withBlockRendering
};